<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import {
  NCard,
  NButton,
  NSpace,
  NTag,
  NList,
  NListItem,
  NAvatar,
  NBadge,
  NIcon,
  NEmpty,
  NSpin,
  NPageHeader,
  NProgress,
  NAlert,
  NDivider,
  NUpload,
  NUploadDragger,
  NModal,
  NPopover,
  NCollapse,
  NCollapseItem,
  NInput,
  NForm,
  NFormItem
} from "naive-ui";
import { 
  User as ProfileIcon, 
  Edit as EditIcon,
  AlertCircle as InfoIcon,
  Stethoscope as MedicalIcon,
  Droplet as Drop,
  Ruler,
  Scale,
  History,
  CheckSquare,
  AlertTriangle,
  Pill,
  FileText,
  User,
  Camera,
  Upload as UploadIcon,
  X as CloseIcon,
  Shield,
  Heart,
  Calendar,
  MessageCircle as Communication,
  Briefcase,
  Users,
  Clipboard,
  Thermometer,
  Award,
  BookOpen,
  Coffee,
  Home,
  CalendarCheck,
  Activity,
  Utensils,
  Accessibility as AccessibilityIcon,
  Lock,
  Key,
  FolderOpen,
  Files
} from "lucide-vue-next";

// API utilities
import { 
  fetchMyPatientProfile, 
  uploadPatientProfileImage, 
  getPatientProfileImage 
} from "@/services/patient";
import { authToken, isAuthenticated } from "@/services/auth";

// Encryption utilities
import { decryptPatientData } from "@/services/encryption";

const router = useRouter();

// Page state
const loading = ref(true);
const error = ref(null);
const patientProfile = ref(null);
const encryptedPatientData = ref(null);
const profileImageUrl = ref(null);
const isUploadingImage = ref(false);
const imageUploadError = ref(null);
const showImageModal = ref(false);

// Password prompt state
const showPasswordModal = ref(false);
const decryptionPassword = ref("");
const passwordError = ref(null);
const isDecrypting = ref(false);

// Computed property to normalize property names
const normalizedProfile = computed(() => {
  if (!patientProfile.value) return null;
  
  const profile = patientProfile.value;
  
  return {
    // Keep original properties
    ...profile,
    
    // Add normalized properties with snake_case names for template compatibility
    full_name: profile.fullName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'Patient',
    emergency_contact_name: profile.emergencyContactName,
    emergency_contact_phone: profile.emergencyContactPhone,
    emergency_contact_relationship: profile.emergencyContactRelationship,
    insurance_provider: profile.insuranceProvider,
    secondary_insurance_provider: profile.secondaryInsuranceProvider,
    insurance_id: profile.insuranceId,
    insurance_group_number: profile.insuranceGroupNumber,
    medical_history: profile.medicalHistory,
    current_medications: profile.currentMedications,
    family_medical_history: profile.familyMedicalHistory,
    surgical_history: profile.surgicalHistory,
    lifestyle_info: profile.lifestyleInfo,
    preferred_pharmacy: profile.preferredPharmacy,
    preferred_pharmacy_address: profile.preferredPharmacyAddress,
    last_physical_exam: profile.lastPhysicalExam,
    primary_care_provider: profile.primaryCareProvider,
    last_visit_date: profile.lastVisitDate,
    accessibility_needs: profile.accessibilityNeeds,
    advance_directives: profile.advanceDirectives,
    dietary_restrictions: profile.dietaryRestrictions,
    preferred_communication_method: profile.preferredCommunicationMethod
  };
});

onMounted(async () => {
  if (!isAuthenticated.value) {
    router.push('/login');
    return;
  }

  await loadPatientData();
});

const loadPatientData = async () => {
  try {
    loading.value = true;
    
    // Fetch encrypted patient profile
    const profileData = await fetchMyPatientProfile(authToken.value);
    
    if (profileData) {
      // Store the encrypted data
      encryptedPatientData.value = profileData;
      
      // Show password prompt to decrypt
      showPasswordModal.value = true;
      
      // Fetch profile image (this is not encrypted)
      try {
        const imageResult = await getPatientProfileImage(authToken.value);
        if (imageResult.success && imageResult.image_url) {
          profileImageUrl.value = imageResult.image_url;
        }
      } catch (imageError) {
        console.error('Error loading profile image:', imageError);
      }
    }
  } catch (err) {
    error.value = "Failed to load patient profile";
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const decryptData = async () => {
  if (!decryptionPassword.value.trim()) {
    passwordError.value = "Please enter your patient password";
    return;
  }

  try {
    isDecrypting.value = true;
    passwordError.value = null;

    const decryptedData = await decryptPatientData(
      encryptedPatientData.value.encrypted_patient_data,
      encryptedPatientData.value.encryption_metadata,
      decryptionPassword.value
    );

    patientProfile.value = decryptedData;

     // ✅ ADD THIS
    console.log('✅ PROFILE - Decryption successful with password:', decryptionPassword.value);
    console.log('✅ PROFILE - Password length:', decryptionPassword.value.length);
    console.log('✅ PROFILE - Password bytes:', Array.from(decryptionPassword.value).map(c => c.charCodeAt(0)));
    sessionStorage.setItem('patientPassword', decryptionPassword.value);
    console.log('💾 PROFILE - Password stored in sessionStorage');


    showPasswordModal.value = false;
    // Store in session storage so it is available for documents.
    sessionStorage.setItem('patientPassword', decryptionPassword.value);

    decryptionPassword.value = "";

  } catch (err) {
    console.error('Decryption failed:', err);
    
    try {
      passwordError.value = "Password alone insufficient. Would you like to use security questions for recovery?";
    } catch (recoveryErr) {
      passwordError.value = "Invalid password. Please try again.";
    }
  } finally {
    isDecrypting.value = false;
  }
};

const cancelDecryption = () => {
  showPasswordModal.value = false;
  decryptionPassword.value = "";
  passwordError.value = null;
  router.push('/patient-profile');
};

// Format date helper with more details
const formatDate = (dateString) => {
  if (!dateString) return "Not provided";
  
  const date = new Date(dateString);
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};

// Format phone number
const formatPhoneNumber = (phone) => {
  if (!phone) return null;
  
  // Basic formatting for a 10-digit number
  if (phone.length === 10) {
    return `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
  }
  
  return phone;
};

// Parse JSON data for display
const parseJsonData = (jsonData) => {
  if (!jsonData || jsonData.length === 0) return [];
  return Array.isArray(jsonData) ? jsonData : [jsonData];
};

// Render lifestyle info in a readable format
const formatLifestyleInfo = (info) => {
  if (!info || Object.keys(info).length === 0) return null;
  
  try {
    const sections = [];
    for (const [key, value] of Object.entries(info)) {
      // Format each key from camelCase or snake_case to readable text
      const formattedKey = key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase());
      
      // Format value based on type
      let formattedValue = value;
      if (typeof value === 'boolean') {
        formattedValue = value ? 'Yes' : 'No';
      } else if (Array.isArray(value)) {
        formattedValue = value.join(', ');
      }
      
      sections.push({ key: formattedKey, value: formattedValue });
    }
    return sections;
  } catch (e) {
    console.error('Error formatting lifestyle info:', e);
    return null;
  }
};

// Navigate to edit profile
const editProfile = () => {
  router.push('/patient-profile/edit');
};

// Navigate to documents page
const goToDocuments = () => {
  router.push('/patient-documents');
};

// Handle image upload
const handleImageUpload = async (options) => {
  const { file } = options;
  
  try {
    isUploadingImage.value = true;
    imageUploadError.value = null;
    
    // Upload the image
    const result = await uploadPatientProfileImage(authToken.value, file.file);
    
    // Update profile image URL
    if (result && result.image_url) {
      profileImageUrl.value = result.image_url;
      options.onFinish();
    }
    
    // Close modal after successful upload
    showImageModal.value = false;
    
  } catch (err) {
    imageUploadError.value = err.message || "Failed to upload image";
    options.onError();
  } finally {
    isUploadingImage.value = false;
  }
};

// Open image upload modal
const openImageModal = () => {
  showImageModal.value = true;
  imageUploadError.value = null;
};

// Close image upload modal
const closeImageModal = () => {
  showImageModal.value = false;
  imageUploadError.value = null;
};

// View larger profile image
const viewLargerImage = () => {
  if (profileImageUrl.value) {
    window.open(profileImageUrl.value, '_blank');
  }
};
</script>

<template>
  <div class="patient-dashboard-container">
    
    <div v-if="loading" class="loading-container">
      <NSpin size="large" />
      <p>Loading your profile...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <NAlert type="error" :title="error" />
    </div>

    <div v-else>
      <!-- Show encrypted state if no decrypted data -->
      <div v-if="!patientProfile && encryptedPatientData" class="encrypted-state">
        <NCard class="encryption-info-card">
          <div class="encryption-info">
            <NIcon size="48" class="lock-icon">
              <Lock />
            </NIcon>
            <h3>Patient Profile Encrypted</h3>
            <p>Your medical data is encrypted and secure. Enter your patient password to view your profile.</p>
            <NButton type="primary" @click="showPasswordModal = true" round>
              <template #icon>
                <NIcon>
                  <Key />
                </NIcon>
              </template>
              Decrypt Profile
            </NButton>
          </div>
        </NCard>
      </div>

      <!-- Show profile if data is decrypted -->
      <template v-if="normalizedProfile">
        <!-- Profile Header Card -->
        <NCard class="profile-header">
          <div class="profile-header-content">
            <div class="profile-avatar-container">
              <NPopover trigger="hover" placement="bottom">
                <template #trigger>
                  <NAvatar 
                    round 
                    size="large" 
                    :src="profileImageUrl" 
                    class="profile-avatar" 
                    @click="viewLargerImage"
                  >
                    <template v-if="!profileImageUrl">
                      <NIcon size="30">
                        <User />
                      </NIcon>
                    </template>
                  </NAvatar>
                </template>
                <div class="avatar-popover">
                  <p>{{ profileImageUrl ? 'Click to view full image' : 'No profile image' }}</p>
                  <NButton 
                    size="small" 
                    @click="openImageModal" 
                    type="primary" 
                    round
                  >
                    <template #icon>
                      <NIcon>
                        <Camera />
                      </NIcon>
                    </template>
                    {{ profileImageUrl ? 'Change Image' : 'Add Image' }}
                  </NButton>
                </div>
              </NPopover>
              
              <NButton 
                class="camera-button" 
                circle 
                type="primary" 
                @click="openImageModal"
                size="small"
              >
                <NIcon>
                  <Camera />
                </NIcon>
              </NButton>
            </div>

            <div class="profile-info">
              <h2>{{ normalizedProfile?.full_name || 'Your Profile' }}</h2>
              
              <div class="profile-status">
                <NTag type="success" round>
                  <template #icon>
                    <NIcon>
                      <Shield />
                    </NIcon>
                  </template>
                  Data Decrypted & Secure
                </NTag>
              </div>
            </div>

            <div class="profile-actions">
              <NButton type="primary" @click="editProfile" round>
                <template #icon>
                  <NIcon>
                    <EditIcon />
                  </NIcon>
                </template>
                Update Profile
              </NButton>
            </div>
          </div>
        </NCard>

        <!-- Quick Actions Card - NEW -->
        <NCard class="quick-actions-card">
          <div class="quick-actions-grid">
            <div class="action-item" @click="goToDocuments">
              <div class="action-icon documents-icon">
                <NIcon size="32">
                  <FolderOpen />
                </NIcon>
              </div>
              <div class="action-content">
                <h3>My Documents</h3>
                <p>View and manage your medical records, notes, and files</p>
              </div>
              <div class="action-arrow">
                <NIcon size="20">
                  <FileText />
                </NIcon>
              </div>
            </div>
          </div>
        </NCard>

        <!-- Personal Info Section -->
        <NCard title="Personal Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <ProfileIcon />
            </NIcon>
          </template>
          
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Date of Birth</div>
              <div class="info-value">{{ formatDate(normalizedProfile.dateOfBirth) }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Gender</div>
              <div class="info-value capitalize">{{ normalizedProfile.gender || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Address</div>
              <div class="info-value">{{ normalizedProfile.fullAddress || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Phone</div>
              <div class="info-value">
                <span class="phone-number">{{ formatPhoneNumber(normalizedProfile.phoneNumber) || 'Not provided' }}</span>
              </div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Preferred Language</div>
              <div class="info-value">{{ normalizedProfile.preferredLanguage || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Preferred Communication</div>
              <div class="info-value">{{ normalizedProfile.preferred_communication_method || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Occupation</div>
              <div class="info-value">{{ normalizedProfile.occupation || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Marital Status</div>
              <div class="info-value capitalize">{{ normalizedProfile.maritalStatus || 'Not provided' }}</div>
            </div>
          </div>
        </NCard>

        <!-- Emergency Contact Section -->
        <NCard title="Emergency Contact" class="profile-section">
          <template #header-extra>
            <NIcon>
              <InfoIcon />
            </NIcon>
          </template>
          
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Contact Name</div>
              <div class="info-value">{{ normalizedProfile.emergency_contact_name || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Contact Phone</div>
              <div class="info-value">
                <span class="phone-number">{{ formatPhoneNumber(normalizedProfile.emergency_contact_phone) || 'Not provided' }}</span>
              </div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Relationship</div>
              <div class="info-value">{{ normalizedProfile.emergency_contact_relationship || 'Not provided' }}</div>
            </div>
          </div>
        </NCard>
        
        <!-- Insurance Information Section -->
        <NCard title="Insurance Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Shield />
            </NIcon>
          </template>
          
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Primary Insurance</div>
              <div class="info-value">{{ normalizedProfile.insurance_provider || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Secondary Insurance</div>
              <div class="info-value">{{ normalizedProfile.secondary_insurance_provider || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Insurance ID</div>
              <div class="info-value">{{ normalizedProfile.insurance_id || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Group Number</div>
              <div class="info-value">{{ normalizedProfile.insurance_group_number || 'Not provided' }}</div>
            </div>
          </div>
        </NCard>

        <!-- Medical Information Section -->
        <NCard title="Medical Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <MedicalIcon />
            </NIcon>
          </template>
          
          <div class="medical-stats">
            <div class="stat-item">
              <div class="stat-icon">
                <NIcon>
                  <Drop />
                </NIcon>
              </div>
              <div class="stat-content">
                <div class="stat-label">Blood Group</div>
                <div class="stat-value">{{ normalizedProfile.bloodGroup || 'Not provided' }}</div>
              </div>
            </div>
            
            <div class="stat-item">
              <div class="stat-icon">
                <NIcon>
                  <Ruler />
                </NIcon>
              </div>
              <div class="stat-content">
                <div class="stat-label">Height</div>
                <div class="stat-value">{{ normalizedProfile.heightCm ? `${normalizedProfile.heightCm} cm` : 'Not provided' }}</div>
              </div>
            </div>
            
            <div class="stat-item">
              <div class="stat-icon">
                <NIcon>
                  <Scale />
                </NIcon>
              </div>
              <div class="stat-content">
                <div class="stat-label">Weight</div>
                <div class="stat-value">{{ normalizedProfile.weightKg ? `${normalizedProfile.weightKg} kg` : 'Not provided' }}</div>
              </div>
            </div>
          </div>

          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <History />
            </NIcon>
            Medical History
          </h3>
          
          <div v-if="normalizedProfile.medical_history && normalizedProfile.medical_history.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in normalizedProfile.medical_history" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <CheckSquare />
                  </NIcon>
                </template>
                {{ item }}
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No medical history provided</p>

          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <AlertTriangle />
            </NIcon>
            Allergies
          </h3>
          
          <div v-if="normalizedProfile.allergies && normalizedProfile.allergies.length > 0">
            <NSpace>
              <NTag 
                v-for="(allergy, index) in normalizedProfile.allergies" 
                :key="index" 
                type="error" 
                round
                size="large"
                class="allergy-tag"
              >
                <template #icon>
                  <NIcon>
                    <AlertTriangle />
                  </NIcon>
                </template>
                {{ allergy }}
              </NTag>
            </NSpace>
          </div>
          <p v-else class="empty-list-message">No allergies provided</p>

          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <Pill />
            </NIcon>
            Current Medications
          </h3>
          
          <div v-if="normalizedProfile.current_medications && normalizedProfile.current_medications.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(med, index) in normalizedProfile.current_medications" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <Pill />
                  </NIcon>
                </template>
                {{ med }}
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No medications provided</p>
          
          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <Clipboard />
            </NIcon>
            Immunizations
          </h3>
          
          <div v-if="normalizedProfile.immunizations && normalizedProfile.immunizations.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in parseJsonData(normalizedProfile.immunizations)" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <CheckSquare />
                  </NIcon>
                </template>
                {{ item.name || item }} {{ item.date ? `(${formatDate(item.date)})` : '' }}
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No immunization records provided</p>
          
          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <Heart />
            </NIcon>
            Family Medical History
          </h3>
          
          <div v-if="normalizedProfile.family_medical_history && normalizedProfile.family_medical_history.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in parseJsonData(normalizedProfile.family_medical_history)" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <Users />
                  </NIcon>
                </template>
                {{ item.condition || item }} {{ item.relation ? `(${item.relation})` : '' }}
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No family medical history provided</p>
          
          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <Thermometer />
            </NIcon>
            Surgical History
          </h3>
          
          <div v-if="normalizedProfile.surgical_history && normalizedProfile.surgical_history.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in parseJsonData(normalizedProfile.surgical_history)" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <Activity />
                  </NIcon>
                </template>
                {{ item.procedure || item }} {{ item.date ? `(${formatDate(item.date)})` : '' }}
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No surgical history provided</p>
          
          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <Coffee />
            </NIcon>
            Lifestyle Information
          </h3>
          
          <div v-if="formatLifestyleInfo(normalizedProfile.lifestyle_info)">
            <div class="lifestyle-info">
              <div 
                v-for="(item, index) in formatLifestyleInfo(normalizedProfile.lifestyle_info)" 
                :key="index"
                class="lifestyle-item"
              >
                <div class="lifestyle-label">{{ item.key }}</div>
                <div class="lifestyle-value">{{ item.value }}</div>
              </div>
            </div>
          </div>
          <p v-else class="empty-list-message">No lifestyle information provided</p>
          
          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <Home />
            </NIcon>
            Pharmacy Information
          </h3>
          
          <div class="info-grid pharmacy-grid">
            <div class="info-item">
              <div class="info-label">Preferred Pharmacy</div>
              <div class="info-value">{{ normalizedProfile.preferred_pharmacy || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Pharmacy Address</div>
              <div class="info-value">{{ normalizedProfile.preferred_pharmacy_address || 'Not provided' }}</div>
            </div>
          </div>
          
          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <CalendarCheck />
            </NIcon>
            Healthcare Records
          </h3>
          
          <div class="info-grid healthcare-grid">
            <div class="info-item">
              <div class="info-label">Last Physical Exam</div>
              <div class="info-value">{{ normalizedProfile.last_physical_exam ? formatDate(normalizedProfile.last_physical_exam) : 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Primary Care Provider</div>
              <div class="info-value">{{ normalizedProfile.primary_care_provider || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Last Visit Date</div>
              <div class="info-value">{{ normalizedProfile.last_visit_date ? formatDate(normalizedProfile.last_visit_date) : 'Not provided' }}</div>
            </div>
          </div>
          
          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <AccessibilityIcon />
            </NIcon>
            Additional Healthcare Information
          </h3>
          
          <div class="additional-info-grid">
            <div class="info-item">
              <div class="info-label">Accessibility Needs</div>
              <div v-if="normalizedProfile.accessibility_needs && normalizedProfile.accessibility_needs.length > 0">
                <NTag 
                  v-for="(need, index) in parseJsonData(normalizedProfile.accessibility_needs)" 
                  :key="index" 
                  type="info" 
                  round
                  size="medium"
                  class="accessibility-tag"
                >
                  {{ need.need || need }}
                </NTag>
              </div>
              <div v-else class="info-value">Not provided</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Advance Directives</div>
              <div class="info-value">
                <NTag :type="normalizedProfile.advance_directives ? 'success' : 'default'" round>
                  {{ normalizedProfile.advance_directives ? 'Yes' : 'Not Specified' }}
                </NTag>
              </div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Dietary Restrictions</div>
              <div v-if="normalizedProfile.dietary_restrictions && normalizedProfile.dietary_restrictions.length > 0">
                <NSpace>
                  <NTag 
                    v-for="(restriction, index) in normalizedProfile.dietary_restrictions" 
                    :key="index" 
                    type="warning" 
                    round
                    size="medium"
                    class="diet-tag"
                  >
                    <template #icon>
                      <NIcon>
                        <Utensils />
                      </NIcon>
                    </template>
                    {{ restriction }}
                  </NTag>
                </NSpace>
              </div>
              <div v-else class="info-value">No dietary restrictions</div>
            </div>
          </div>

          <NDivider v-if="normalizedProfile.notes" />

          <div v-if="normalizedProfile.notes">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <FileText />
              </NIcon>
              Additional Notes
            </h3>
            <div class="notes-box">{{ normalizedProfile.notes }}</div>
          </div>
        </NCard>
      </template>

      <!-- Show empty state if no profile exists -->
      <NCard v-if="!encryptedPatientData && !patientProfile" class="profile-details">
        <NEmpty description="No patient profile yet">
          <template #extra>
            <p>You haven't created your patient profile yet. Create your profile to better manage your healthcare information.</p>
            <NButton type="primary" @click="editProfile">Create Profile</NButton>
          </template>
        </NEmpty>
      </NCard>
    </div>
  </div>

  <!-- Password Decryption Modal -->
  <NModal 
    v-model:show="showPasswordModal" 
    preset="card" 
    title="Decrypt Patient Profile" 
    :closable="false"
    :mask-closable="false"
    :style="{ width: '400px' }"
  >
    <div class="password-modal-content">
      <div class="password-icon">
        <NIcon size="48">
          <Lock />
        </NIcon>
      </div>
      
      <p>Enter your patient password to decrypt and view your medical data.</p>
      
      <NForm>
        <NFormItem>
          <NInput
            v-model:value="decryptionPassword"
            type="password"
            placeholder="Patient Password"
            size="large"
            :disabled="isDecrypting"
            @keyup.enter="decryptData"
          />
        </NFormItem>
      </NForm>
      
      <div v-if="passwordError" class="password-error">
        <NAlert type="error" :title="passwordError" />
      </div>
    </div>
    
    <template #footer>
      <div class="modal-footer">
        <NSpace>
          <NButton @click="cancelDecryption" :disabled="isDecrypting">
            Cancel
          </NButton>
          <NButton 
            type="primary" 
            @click="decryptData" 
            :loading="isDecrypting"
            :disabled="!decryptionPassword.trim()"
          >
            <template #icon>
              <NIcon>
                <Key />
              </NIcon>
            </template>
            Decrypt Profile
          </NButton>
        </NSpace>
      </div>
    </template>
  </NModal>

  <!-- Profile Image Upload Modal -->
  <NModal v-model:show="showImageModal" preset="card" title="Upload Profile Picture" :style="{ width: '400px' }">
    <template #header-extra>
      <NButton circle size="small" @click="closeImageModal">
        <NIcon><CloseIcon /></NIcon>
      </NButton>
    </template>
    
    <div class="upload-modal-content">
      <div v-if="imageUploadError" class="upload-error">
        <NAlert type="error" :title="imageUploadError" closable />
      </div>
      
      <NUpload
        :custom-request="handleImageUpload"
        :max="1"
        accept="image/*"
        :show-file-list="false"
      >
        <NUploadDragger>
          <div class="upload-dragger-content">
            <NIcon size="48" class="upload-icon">
              <UploadIcon />
            </NIcon>
            <p>Click or drag image to this area to upload</p>
            <p class="upload-hint">
              Support for JPEG, PNG, or GIF, max size 2MB
            </p>
          </div>
        </NUploadDragger>
      </NUpload>
      
      <div v-if="isUploadingImage" class="upload-loading">
        <NSpin size="medium" />
        <p>Uploading your image...</p>
      </div>
    </div>
    
    <template #footer>
      <div class="modal-footer">
        <NButton @click="closeImageModal">Cancel</NButton>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
.patient-dashboard-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

/* Encrypted state styling */
.encrypted-state {
  margin-bottom: 20px;
}

.encryption-info-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #eaeffa;
}

.encryption-info {
  text-align: center;
  padding: 40px 20px;
}

.lock-icon {
  color: #1890ff;
  margin-bottom: 20px;
}

.encryption-info h3 {
  margin: 0 0 16px 0;
  color: #0e639c;
  font-size: 1.4rem;
}

.encryption-info p {
  margin: 0 0 24px 0;
  color: #666;
  font-size: 1rem;
}

.profile-header {
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background-color: #f9fbff;
  border: 1px solid #eaeffa;
}

.profile-header-content {
  display: flex;
  align-items: center;
  padding: 16px;
}

.profile-avatar-container {
  position: relative;
  margin-right: 24px;
}

.profile-avatar {
  cursor: pointer;
  width: 80px;
  height: 80px;
  font-size: 32px;
  background: linear-gradient(135deg, #2979ff, #1890ff);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
  transition: transform 0.2s ease;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.camera-button {
  position: absolute;
  right: -5px;
  bottom: -5px;
  z-index: 1;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.avatar-popover {
  padding: 8px;
  text-align: center;
}

.avatar-popover p {
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  color: #666;
}

.profile-info {
  flex: 1;
}

.profile-info h2 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 1.5rem;
  color: #0e639c;
  font-weight: 600;
}

.profile-status {
  margin-top: 8px;
}

.profile-actions {
  margin-left: 24px;
}

/* NEW: Quick Actions Card Styling */
.quick-actions-card {
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #eaeffa;
}

.quick-actions-grid {
  display: grid;
  gap: 16px;
}

.action-item {
  display: flex;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
  border-radius: 10px;
  border: 2px solid #e3f2fd;
  cursor: pointer;
  transition: all 0.3s ease;
  gap: 16px;
}

.action-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 20px rgba(24, 144, 255, 0.15);
  border-color: #1890ff;
}

.action-icon {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.documents-icon {
  background: linear-gradient(135deg, #2979ff, #1890ff);
  color: white;
}

.action-item:hover .action-icon {
  transform: scale(1.1);
}

.action-content {
  flex: 1;
}

.action-content h3 {
  margin: 0 0 6px 0;
  font-size: 1.2rem;
  color: #0e639c;
  font-weight: 600;
}

.action-content p {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
}

.action-arrow {
  flex-shrink: 0;
  color: #1890ff;
  transition: transform 0.3s ease;
}

.action-item:hover .action-arrow {
  transform: translateX(4px);
}

.profile-section {
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid #eaeffa;
}

.profile-section :deep(.n-card-header) {
  background-color: #f5f8ff;
  font-size: 1.1rem;
  font-weight: 600;
  color: #0e639c;
  padding: 16px 24px;
}

.profile-section :deep(.n-card__content) {
  padding: 20px 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 8px;
}

.info-item {
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 10px;
  transition: all 0.2s ease;
  border-left: 4px solid #1890ff;
}

.info-item:hover {
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.info-label {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;
}

.info-value {
  font-size: 1.1rem;
  color: #333;
  font-weight: 500;
}

.capitalize {
  text-transform: capitalize;
}

.phone-number {
  font-family: monospace;
  letter-spacing: 0.5px;
  background-color: rgba(24, 144, 255, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.medical-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.stat-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 10px;
  transition: all 0.2s ease;
  border-left: 4px solid #1890ff;
}

.stat-item:hover {
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  margin-right: 16px;
  color: #1890ff;
  background-color: rgba(24, 144, 255, 0.1);
  padding: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 6px;
  font-weight: 500;
}

.stat-value {
  font-size: 1.1rem;
  color: #333;
  font-weight: 600;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  color: #0e639c;
  margin-top: 24px;
  margin-bottom: 16px;
}

.section-icon {
  margin-right: 8px;
  color: #1890ff;
}

.styled-list :deep(.n-list-item) {
  padding: 12px 16px;
  transition: background-color 0.2s ease;
}

.styled-list :deep(.n-list-item:hover) {
  background-color: #f0f7ff;
}

.allergy-tag, .accessibility-tag, .diet-tag {
  margin: 4px;
  padding: 6px 12px;
  font-weight: 500;
  transition: transform 0.2s ease;
}

.allergy-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(220, 50, 50, 0.2);
}

.accessibility-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.2);
}

.diet-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(255, 153, 0, 0.2);
}

.empty-list-message {
  color: #8c9bab;
  font-style: italic;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  text-align: center;
  font-size: 0.95rem;
}

.notes-box {
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #1890ff;
  white-space: pre-line;
  font-style: italic;
}

.lifestyle-info {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
}

.lifestyle-item {
  background-color: #f9f9f9;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #1890ff;
}

.lifestyle-label {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 4px;
  font-weight: 500;
}

.lifestyle-value {
  font-size: 1rem;
  color: #333;
}

.pharmacy-grid, .healthcare-grid, .additional-info-grid {
  margin-top: 16px;
}

/* Password modal styling */
.password-modal-content {
  padding: 20px 0;
  text-align: center;
}

.password-icon {
  color: #1890ff;
  margin-bottom: 20px;
}

.password-modal-content p {
  margin-bottom: 20px;
  color: #666;
}

.password-error {
  margin-top: 16px;
}

/* Upload modal styling */
.upload-modal-content {
  padding: 16px 0;
}

.upload-dragger-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.upload-icon {
  color: #1890ff;
  margin-bottom: 16px;
}

.upload-hint {
  font-size: 0.85rem;
  color: #999;
  margin-top: 8px;
}

.upload-error {
  margin-bottom: 16px;
}

.upload-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .patient-dashboard-container {
    padding: 10px;
  }
  
  .profile-header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .profile-avatar-container {
    margin-right: 0;
    margin-bottom: 15px;
  }
  
  .profile-actions {
    margin-left: 0;
    margin-top: 15px;
  }

  .action-item {
    padding: 16px;
  }

  .action-icon {
    width: 50px;
    height: 50px;
  }

  .action-content h3 {
    font-size: 1.1rem;
  }

  .action-content p {
    font-size: 0.85rem;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .medical-stats {
    flex-direction: column;
  }
  
  .lifestyle-info {
    grid-template-columns: 1fr;
  }
}
</style>